/**
 * Recent Form Algorithm
 *
 * Component-based Monte Carlo prediction using the last 5 races
 * with equal weighting. Balanced view of current performance.
 */

import { RECENT_FORM_CONFIG } from "../../prediction";
import {
  estimateConstructorParams,
  estimateDriverParams,
} from "../../prediction/component-model";
import { runDriverMonteCarlo } from "../../prediction/monte-carlo";
import { computeWeights, weightedMean } from "../../prediction/recency-weights";
import { adjustParamsForStage } from "../../prediction/stage-adjustments";
import { getTrackClassification } from "../../statistics/track-classification";
import { calculateSimplePriceChange } from "../price-change";
import type {
  PredictionAlgorithm,
  PredictionResult,
  TeamCalculatorConstructor,
  TeamCalculatorDriver,
  TeamCalculatorRaceContext,
} from "../types";

function buildRaceContext(raceContext: TeamCalculatorRaceContext) {
  return {
    raceSlug: raceContext.raceName ?? "",
    trackType: getTrackClassification(raceContext.raceName ?? ""),
    isSprintWeekend: raceContext.isSprintWeekend,
    raceRound: raceContext.raceRound,
    season: 2025,
  };
}

export const recentFormAlgorithm: PredictionAlgorithm = {
  metadata: {
    id: "recent-form",
    name: "Recent Form",
    description:
      "Focuses on the last 5 races for a balanced view of current performance. Good default choice.",
    icon: "TrendingUp",
  },

  predictDriver(
    driver: TeamCalculatorDriver,
    raceContext: TeamCalculatorRaceContext,
  ): PredictionResult {
    const ctx = buildRaceContext(raceContext);
    const baseParams = estimateDriverParams(driver, ctx, RECENT_FORM_CONFIG);
    const params = adjustParamsForStage(
      baseParams,
      driver,
      raceContext.currentStage,
    );
    const result = runDriverMonteCarlo(
      params,
      raceContext.isSprintWeekend,
      10_000,
      raceContext.season,
    );

    return {
      entityId: driver.driverSlug,
      predictedPoints: Math.round(result.expectedPoints * 10) / 10,
      confidence: result.confidence,
      p10: result.p10,
      p90: result.p90,
      stdDev: result.stdDev,
      dnfProb: result.dnfProb,
      components: result.components,
    };
  },

  predictConstructor(
    ctor: TeamCalculatorConstructor,
    raceContext: TeamCalculatorRaceContext,
  ): PredictionResult {
    const history = ctor.raceHistory;

    if (history.length === 0) {
      return {
        entityId: ctor.slug,
        predictedPoints: 20,
        confidence: 0.1,
      };
    }

    const reversed = [...history].reverse();
    const weights = computeWeights(reversed.length, RECENT_FORM_CONFIG);
    const points = reversed.map((h) => h.totalPoints);
    let avgPoints = weightedMean(points, weights);

    // Sprint-aware: blend sprint-specific average when predicting for sprint weekend
    if (raceContext.isSprintWeekend) {
      const sprintRaces = reversed.filter((h) => h.isSprintWeekend);
      if (sprintRaces.length >= 2) {
        const sprintPts = sprintRaces.map((h) => h.totalPoints);
        const sprintAvg =
          sprintPts.reduce((a, b) => a + b, 0) / sprintPts.length;
        avgPoints = sprintAvg * 0.7 + avgPoints * 0.3;
      }
    }

    const ctorParams = estimateConstructorParams(ctor, RECENT_FORM_CONFIG);

    return {
      entityId: ctor.slug,
      predictedPoints: Math.round(avgPoints * 10) / 10,
      confidence: ctorParams.confidence,
    };
  },

  calculatePriceChange(
    predictedPoints: number,
    currentPrice: number,
    tier: "A" | "B",
    priceFloor: number,
  ): number {
    return calculateSimplePriceChange(
      predictedPoints,
      tier,
      currentPrice,
      priceFloor,
    );
  },
};
